import { StrictMode } from "react";
import { createRoot } from "react-dom/client";
import App from "./app";
import "./index.css";
import "./handwriting-font.css";
import "./display-font.css";
import "./glass-interaction.css";
import "./material-alpha.css";
import { AuthProvider } from "@/hooks/use-auth";
import { GlassSurfaceFilter } from "@/components/ui";
import { initPostHog } from "@/lib/posthog";
import { initSentry } from "@/lib/sentry";
import { hydrateStoredThemeChoice } from "@/lib/theme";
import { hydrateStoredTransparency } from "@/lib/transparency";
import { captureSignupAttribution } from "@/lib/signup-attribution";
import { captureAttributionToken } from "@/lib/attribution-token";
import { initAppLanguage } from "@/stores/app-language-store";

async function main() {
  captureSignupAttribution();
  captureAttributionToken();
  hydrateStoredThemeChoice();
  hydrateStoredTransparency();
  // The catalogues load before the tree mounts, so the first frame is
  // already in the app language.
  await initAppLanguage();

  if (window.location.hostname.startsWith("preview")) {
    document.title = "Denker (Preview)";
  }

  const Sentry = await initSentry();
  initPostHog();

  const app = (
    <AuthProvider>
      <App />
    </AuthProvider>
  );
  // [DEBUG-COMPACT] visible fallback so we can see when an error boundary
  // catches a render error in compact mode (window is transparent
  // click-through; an invisible <p>Something went wrong.</p> would just
  // make the pill silently disappear).
  const fallback = ({ error }: { error: unknown }) => {
    const msg =
      error instanceof Error
        ? `${error.name}: ${error.message}`
        : String(error);
    console.error("[ErrorBoundary fallback]", error);
    return (
      <div
        data-interactive="true"
        style={{
          position: "fixed",
          top: 8,
          left: 8,
          right: 8,
          padding: "12px 16px",
          background: "rgba(220, 38, 38, 0.95)",
          color: "white",
          fontFamily: "ui-monospace, monospace",
          fontSize: 12,
          borderRadius: 8,
          zIndex: 99999,
          boxShadow: "0 4px 12px rgba(0,0,0,0.3)",
        }}
      >
        <div style={{ fontWeight: 600, marginBottom: 4 }}>
          React error caught — UI replaced by fallback
        </div>
        <div style={{ whiteSpace: "pre-wrap", wordBreak: "break-word" }}>
          {msg}
        </div>
      </div>
    );
  };
  createRoot(document.getElementById("root")!).render(
    <StrictMode>
      <GlassSurfaceFilter />
      {Sentry ? (
        <Sentry.ErrorBoundary fallback={fallback}>{app}</Sentry.ErrorBoundary>
      ) : (
        app
      )}
    </StrictMode>,
  );
}

main();
